const INVALID_VALUE_PATTERN = /^(.+?)\s+is\s+invalid\s+value\.?$/i;
const REQUIRED_PATTERN = /^(.+?)\s+is\s+required\.?$/i;
const MUTUALLY_EXCLUSIVE_PATTERN = /^assigneeLead and assigneeGroup are mutually exclusive\.?$/i;

const GENERIC_CREATE_FAILURE_DETAILS = new Set([
    'failed to create report.',
    'create report failed.',
    'create car report failed.',
    'create car report failed from api.',
]);

const FIELD_LABELS: Record<string, string> = {
    ufi: 'UFI',
    category: 'Category',
    subject: 'Subject',
    comments: 'Comment',
    creator: 'Creator',
    airport: 'Airport',
    chartref: 'Chart Ref',
    photoids: 'Photo',
    assigneelead: 'Assignee lead',
    assigneegroup: 'Assignee group',
};

const toTrimmedString = (value: unknown): string => String(value ?? '').trim();

const ensureSentence = (value: string): string => {
    const trimmed = toTrimmedString(value);
    if (!trimmed) return '';

    const withLeadingCapital = `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}`;
    return /[.!?]$/.test(withLeadingCapital) ? withLeadingCapital : `${withLeadingCapital}.`;
};

const toFieldLabel = (value: string): string => {
    const trimmed = toTrimmedString(value);
    if (!trimmed) return '';

    const mapped = FIELD_LABELS[trimmed.toLowerCase()];
    if (mapped) return mapped;

    return trimmed
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
};

export const extractErrorMessage = (error: any, fallback = ''): string => {
    const candidates = [
        error?.detail,
        error?.errors?.detail,
        error?.data?.errors?.detail,
        error?.response?.data?.errors?.detail,
        error?.message,
        error?.msg,
        error?.data?.msg,
        error?.response?.data?.msg,
        fallback,
    ];

    for (const candidate of candidates) {
        const text = toTrimmedString(candidate);
        if (text) return text;
    }

    return '';
};

const normalizeCreateReportDetail = (detail: string): string => {
    const normalized = toTrimmedString(detail);
    if (!normalized) return '';

    if (MUTUALLY_EXCLUSIVE_PATTERN.test(normalized)) {
        return 'Assignee lead and assignee group cannot be selected together.';
    }

    const requiredMatch = normalized.match(REQUIRED_PATTERN);
    if (requiredMatch) {
        return ensureSentence(`${toFieldLabel(requiredMatch[1])} is required`);
    }

    const invalidValueMatch = normalized.match(INVALID_VALUE_PATTERN);
    if (invalidValueMatch) {
        return ensureSentence(`${toFieldLabel(invalidValueMatch[1])} is an invalid value`);
    }

    return ensureSentence(normalized);
};

export const formatCreateReportErrorMessage = (error: any, fallback = 'Failed to create report.'): string => {
    const fallbackSentence = ensureSentence(fallback) || 'Failed to create report.';
    const detail = normalizeCreateReportDetail(extractErrorMessage(error));

    if (!detail) return fallbackSentence;
    if (GENERIC_CREATE_FAILURE_DETAILS.has(detail.toLowerCase())) return fallbackSentence;

    return `${fallbackSentence.replace(/[.!?]$/, '')}. ${detail}`;
};

export const formatAddRemarkErrorMessage = (
    error: any,
    fallback = 'Add remark failed.',
    retryHint = 'Please add the remark again.',
): string => {
    const fallbackSentence = ensureSentence(fallback) || 'Add remark failed.';
    const retrySentence = ensureSentence(retryHint) || 'Please add the remark again.';
    const detail = ensureSentence(extractErrorMessage(error));

    if (!detail) return `${fallbackSentence} ${retrySentence}`;

    return `${fallbackSentence} ${detail} ${retrySentence}`;
};
